import { Headphones, Radio, X } from "lucide-react";

import { cn } from "../../../lib/utils";
import { useAudioSession } from "./audio-provider";

/**
 * A persistent marker of which call you are actually hearing.
 *
 * Now that audio outlives the selection, the connected call and the call on screen can be
 * two different things -- which is the point, but it means "whose voice is this?" stops
 * being obvious. The dock answers that continuously, and gives a one-click way back to the
 * call in question.
 *
 * It is deliberately not a player: no pause, no volume. A supervisor who wants to stop
 * listening should leave the call, not mute a conversation they are supposed to be
 * monitoring while the tile still says they are on it.
 */
export function AudioDock({
  onFocusCall,
  label,
  openCallId,
}: {
  onFocusCall: (callId: string) => void;
  label?: string;
  /** The call open in the drawer. Its toolbar already shows this, and the dock covered it. */
  openCallId?: string | null;
}) {
  const session = useAudioSession();
  if (!session.callId || session.callId === openCallId) return null;

  const holding = session.tier === "takeover";

  return (
    <div
      data-testid="supervision-audio-dock"
      className={cn(
        "fixed bottom-4 right-4 z-40 flex items-center gap-3 rounded-xl border px-3 py-2 shadow-lg",
        // Solid, never translucent: it sits over live transcript text.
        holding ? "border-warning/50 bg-warning-soft" : "border-line bg-elevated",
      )}
    >
      <span
        className={cn(
          "flex items-center gap-1.5 text-xs font-medium",
          holding ? "text-warning" : "text-success",
        )}
      >
        {holding ? <Radio size={13} /> : <Headphones size={13} />}
        {holding ? "On air" : "Listening"}
      </span>

      <button
        type="button"
        onClick={() => onFocusCall(session.callId as string)}
        className="max-w-[200px] truncate text-xs text-ink underline-offset-2 hover:underline"
        title="Go to this call"
      >
        {label || session.callId}
      </button>

      {/* Ending a takeover from here would skip the briefing, so the dock only offers the
          safe exit; handing back stays with the controls that can brief the AI. */}
      {holding ? (
        <span className="text-[11px] text-ink-3">Hand back from the call</span>
      ) : (
        <button
          type="button"
          onClick={() => void session.stop()}
          title="Stop listening"
          className="rounded p-1 text-ink-3 transition hover:text-danger"
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
}
